import React, { useState, useCallback, useEffect, useMemo } from 'react';

import { Column } from 'primereact/column';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { Container } from './styles';

import GerenciadorArquivoService from '../../services/GerenciadorArquivoService';
import AuthService from '../../services/AuthService';
import history from '../../services/history';
import {
  getPageParams,
  errorHandle,
  padLeft,
  calcNaxItemsPage,
  isScreenMobile,
  formatDate,
} from '../../util/functions';

import { showMessage } from '../../components/MessageDialog';

import BotaoMenuGrid from '../../components/BotaoMenuGrid';

import GerenciadorArquivoCrud from './crud';
import { StateScreen } from '../constants';
import CrudHeader from '../../components/CrudHeader';
import DataTableSp from '../../components/DataTableSp';
import LabelSp from '../../components/LabelSp';
import DropDownLazy from '../../components/DropdownLazy';
import ArquivoCategoriaService from '../../services/ArquivoCategoriaService';
import ArquivoSubcategoriaService from '../../services/ArquivoSubcategoriaService';
import ButtonSp from '../../components/ButtonSp';
import { cfgPtBr } from '../../config/Constantes';
import CalendarSp from '../../components/CalendarSp';

export default function GerenciadorArquivo(props) {
  // useSelectors
  const filterVisible = useSelector(state => state.global.filterVisible);

  // useMemo
  const pageParams = useMemo(() => getPageParams(props.match.params, props.location.search), [
    props.location.search,
    props.match.params,
  ]);

  const filterService = useMemo(() => GerenciadorArquivoService.getFilter(), []);

  // useStates
  const toBack = pageParams.toBack || '/gerenciadorArquivos';

  const [filter, setFilter] = useState(filterService);
  const [gerenciadorArquivos, setGerenciadorArquivoes] = useState([]);

  const [pageLimit, setPageLimit] = useState(filterService.limit);
  const [first, setFirst] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSelected, setPageSelected] = useState(0);

  const [funcTimeOut, setFuncTimeOut] = useState();

  const [arquivoCategoriaSelecionado, setArquivoCategoriaSelecionado] = useState(null);
  const [arquivoSubcategoriaSelecionado, setArquivoSubcategoriaSelecionado] = useState(null);

  // useCallbacks

  const calcLimit = useCallback(() => {
    let ret;
    if (filterVisible) {
      ret = calcNaxItemsPage(10, 14, 19);
    } else {
      ret = calcNaxItemsPage(13, 17, 22);
    }
    setPageLimit(ret);
    return ret;
  }, [filterVisible]);

  const loadArquivoCategoria = useCallback(async _nome => {
    if (_nome !== undefined) {
      const r = await ArquivoCategoriaService.findAll({
        nome: _nome,
        limit: 50,
      });

      const retorno = r.items.map(e => {
        return {
          label: e.nome,
          value: e.id,
        };
      });
      return retorno;
    }
    return [];
  }, []);

  const loadArquivoSubcategoria = useCallback(async _nome => {
    if (_nome !== undefined) {
      const r = await ArquivoSubcategoriaService.findAll({
        nome: _nome,
        limit: 50,
      });

      const retorno = r.items.map(e => {
        return {
          label: e.nome,
          value: e.id,
        };
      });
      return retorno;
    }
    return [];
  }, []);

  const handleBuscar = useCallback(
    async (_filter, _page, resetPage = true) => {
      _filter.page = _page || 0;
      _filter.limit = calcLimit();
      try {
        const result = await GerenciadorArquivoService.findAll(_filter);
        setGerenciadorArquivoes(result.items);

        setTotalRecords(result.totalRecords);
        if (resetPage) {
          setFirst(0);
        }
      } catch (err) {
        errorHandle(err);
      }
    },
    [calcLimit]
  );

  const setFilterAndSearch = useCallback(
    async _filterValue => {
      if (JSON.stringify(_filterValue) !== JSON.stringify(filter)) {
        setFilter(_filterValue);

        if (funcTimeOut) {
          clearTimeout(funcTimeOut);
        }
        const func = setTimeout(async () => {
          handleBuscar(_filterValue);
        }, 800);
        setFuncTimeOut(func);
      }
    },
    [filter, funcTimeOut, handleBuscar]
  );

  function onPage(event) {
    const pagina = event.first / event.rows;
    setPageSelected(pagina);
    setFirst(event.first);
    handleBuscar(filter, pagina, false);
  }

  // useCallbacks
  const excluirRegistro = useCallback(
    async _id => {
      try {
        await GerenciadorArquivoService.delete(_id);
        toast.success('Registro excluído com sucesso.');
        handleBuscar(filter);
      } catch (err) {
        errorHandle(err);
      }
    },
    [filter, handleBuscar]
  );

  // functions
  function getTitle() {
    const titleDefault = 'Gerenciador de Arquivos';
    let titleAdd = '';

    if (pageParams.stateScreen === StateScreen.stSearch) {
      titleAdd = '';
    }
    if (pageParams.stateScreen === StateScreen.stInsert) {
      titleAdd = '(Novo)';
    }
    if (pageParams.stateScreen === StateScreen.stUpdate) {
      titleAdd = `(Alterando Id: ${pageParams.idSelected})`;
    }
    if (pageParams.stateScreen === StateScreen.stView) {
      titleAdd = ` (Visualizando Id: ${pageParams.idSelected})`;
    }

    if (!isScreenMobile()) {
      return `${titleDefault} ${titleAdd}`;
    }

    return titleDefault;
  }

  const confirmaExclusao = useCallback(
    id => {
      showMessage('Confirmação', 'Confirma a exclusão do registro?', idx => {
        if (idx === 1) {
          excluirRegistro(id);
        }
      });
    },
    [excluirRegistro]
  );

  // useEffects
  useEffect(() => {
    if (pageParams.stateScreen === StateScreen.stSearch) {
      handleBuscar(filter, 0, true);
    }
    // desativado para evitar que a cada vez que o arquivoCategoria digitasse o arquivoCategoria buscasse
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffects
  // Responsavel por recalcular a quantidade de registros da grid quando a div do filtro ocultar
  // ou ficar visivel
  useEffect(() => {
    if (pageParams.stateScreen === StateScreen.stSearch) {
      calcLimit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calcLimit]);

  // renders
  return (
    <Container className="container-page page-enter-tab">
      <div className="p-grid">
        <CrudHeader
          title={getTitle()}
          disabledButtonInsert={!AuthService.checkRoles('GERENCIADOR_ARQUIVO_INSERIR')}
          showButtonFilter={pageParams.stateScreen === StateScreen.stSearch}
          showButtonConfig
          showButtonInsert
          showButtonSearch
          titleFilterDefault="Buscar por Nome"
          handleFilterDefault={text => {
            filter.nomeArquivo = text;
            setFilter({ ...filter, nomeArquivo: text });
            handleBuscar(filter);
          }}
          handleClearFilters={() => {
            setFilter({
              ...filter,
              nomeArquivo: '',
            });
          }}
          handleButtonInsert={() => history.push('/gerenciadorArquivos/insert')}
          handleButtonSearch={() => handleBuscar(filter)}
        >
          <div
            className="p-grid p-col-12 p-sm-5 p-lg-5 p-fluid"
            style={{ margin: 0, padding: 0 }}
          >
            <div className="p-col-6 p-fluid">
              <LabelSp htmlFor="dataInicial">De: </LabelSp>
              <CalendarSp
                showIcon
                showOnFocus={false}
                appendTo={document.body}
                dateFormat="dd/mm/yy"
                yearNavigator
                locale={cfgPtBr}
                id="dataInicial"
                value={filter.dataInicio}
                yearRange="2010:2040"
                onChange={e => {
                  setFilterAndSearch({ ...filter, dataInicio: e.value });
                }}
              />
            </div>
            <div className="p-col-6 p-fluid">
              <LabelSp htmlFor="dataFinal">Até: </LabelSp>
              <CalendarSp
                appendTo={document.body}
                showIcon
                showOnFocus={false}
                locale={cfgPtBr}
                id="dataFinal"
                dateFormat="dd/mm/yy"
                value={filter.dataFim}
                yearNavigator
                yearRange="2010:2040"
                onChange={e => setFilterAndSearch({ ...filter, dataFim: e.value })}
              />
            </div>
          </div>
          <div className="p-col-12 p-sm-4 p-lg-4 p-fluid">
            <LabelSp>Categoria</LabelSp>
            <DropDownLazy
              autoLoad
              placeholder="Todos"
              showClear
              onChange={e => {
                setArquivoCategoriaSelecionado(e);
                setFilterAndSearch({ ...filter, idArquivoCategoria: e?.value });
              }}
              value={arquivoCategoriaSelecionado}
              onFilter={async txtFilter => {
                const retorno = await loadArquivoCategoria(txtFilter);
                return retorno;
              }}
            />
          </div>
          <div className="p-col-12 p-sm-3 p-lg-3 p-fluid">
            <LabelSp>Subcategoria</LabelSp>
            <DropDownLazy
              autoLoad
              placeholder="Todos"
              showClear
              onChange={e => {
                setArquivoSubcategoriaSelecionado(e);
                setFilterAndSearch({ ...filter, idArquivoSubcategoria: e?.value });
              }}
              value={arquivoSubcategoriaSelecionado}
              onFilter={async txtFilter => {
                const retorno = await loadArquivoSubcategoria(txtFilter);
                return retorno;
              }}
            />
          </div>
        </CrudHeader>
        {pageParams.stateScreen === StateScreen.stSearch ? renderSearch() : renderCrud()}
      </div>
    </Container>
  );

  function renderSearch() {
    return (
      <>
        <div className="p-col-12 p-fluid datatable-doc-demo">
          <DataTableSp
            value={gerenciadorArquivos}
            style={{ marginBottom: '2px' }}
            paginator
            rows={pageLimit}
            lazy
            responsive
            totalRecords={totalRecords}
            first={first}
            onPage={onPage}
          >
            <Column
              field="id"
              body={rowData => padLeft(rowData.id, 6)}
              header="Id"
              className="grid-col-id"
            />
            <Column
              field="data"
              className="grid-col-data"
              header="Data"
              body={rowData => formatDate(rowData.data, 'dd/MM/yyyy')}
            />
            <Column
              className="grid-col"
              header="Categoria"
              style={{ width: 200 }}
              body={rowData => (rowData.arquivoCategoria ? rowData.arquivoCategoria.nome : '')}
            />
            <Column
              className="grid-col"
              header="Subcategoria"
              style={{ width: 200 }}
              body={rowData =>
                rowData.arquivoSubcategoria ? rowData.arquivoSubcategoria.nome : ''
              }
            />
            <Column className="grid-col" header="Nome do Arquivo" field="nomeArquivo" />
            <Column
              className="gid-col-acoes-35"
              bodyStyle={{ textAlign: 'end' }}
              body={renderButtonDownload}
            />
            <Column
              className="gid-col-acoes-35"
              bodyStyle={{ textAlign: 'end' }}
              body={renderButtonOp}
            />
          </DataTableSp>
        </div>
      </>
    );
  }

  function renderButtonDownload(rowData) {
    let icone = 'pi pi-download';
    let label = 'Download';
    let classButton = 'botao-pequeno p-button-success buttons';
    switch (rowData.tipoArquivo) {
      case 'PDF':
        icone = 'pi pi-file-pdf';
        label = 'Visualizar PDF';
        classButton = 'botao-pequeno p-button-success buttons';
        break;
      case 'JPG':
      case 'JPEG':
      case 'PNG':
        icone = 'pi pi-image';
        label = 'Visualizar Imagem';
        classButton = 'botao-pequeno p-button-success buttons';
        break;
      default:
        break;
    }

    return (
      <>
        <ButtonSp
          className={classButton}
          title={label}
          icon={icone}
          onClick={() => getFile(rowData.nomeArquivo, rowData.uri)}
        />
      </>
    );
  }

  async function getFile(nomeArquivo, uri) {
    let file;

    try {
      file = await GerenciadorArquivoService.getFile(uri);
    } catch (err) {
      errorHandle(err);
    }
    if (file) {
      const blob = new Blob([file]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nomeArquivo);
      document.body.appendChild(link);
      link.click();
      document.body.appendChild(link);
    }
  }

  function renderButtonOp(rowData) {
    return (
      <BotaoMenuGrid
        handles={[
          () => history.push(`/gerenciadorArquivos/${rowData.id}?view`),
          () => history.push(`/gerenciadorArquivos/${rowData.id}`),
          () => confirmaExclusao(rowData.id),
        ]}
        disableds={[
          false,
          !AuthService.checkRoles('GERENCIADOR_ARQUIVO_ALTERAR'),
          !AuthService.checkRoles('GERENCIADOR_ARQUIVO_EXCLUIR'),
        ]}
      />
    );
  }

  function renderCrud() {
    return (
      <GerenciadorArquivoCrud
        idSelected={pageParams.idSelected}
        stateScreen={pageParams.stateScreen}
        onClose={() => {
          history.push(toBack);
          handleBuscar(filter, pageSelected, false);
        }}
      />
    );
  }
}
